import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { NotFound } from '@/pages/errors/NotFound';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useGoBack } from '@/hooks/useGoBack';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { buildChannel, IN_SCOPE_CHANNEL_IDS } from '@/lib/channels';
import { ProductReservationsSetup } from '@/pages/channels/setup/ProductReservationsSetup';
import { LocalCheckoutSetup } from '@/pages/channels/setup/LocalCheckoutSetup';
import { MetaCommerceSetup } from '@/pages/channels/setup/MetaCommerceSetup';

const SETUP_COMPONENTS = {
	'product-reservations': ProductReservationsSetup,
	'local-checkout': LocalCheckoutSetup,
	'meta-commerce': MetaCommerceSetup
};

function SetupLoading() {
	return (
		<div className="space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-10 w-72" />
			<Skeleton className="h-96" />
		</div>
	);
}

/**
 * Channel setup wizard host. Per-channel components live in
 * `pages/channels/setup/` and own their own multi-step state; this dispatcher
 * just resolves the channel, renders the shared header, and redirects to the
 * manage page once the channel is live.
 *
 * Channels with `enablement: 'enable'`, `register-interest`, `ads-budget`,
 * or `launch-storefront` don't have a setup page — those are sent back to
 * the detail route.
 */
// Setup steps allowed to render when the channel is already live — Meta's
// post-enable "Create your shop" courtesy view is the only one today; every
// other live channel goes to /manage.
const LIVE_ALLOWED_STEPS = new Set(['create-shop']);

export function ChannelSetup() {
	const { id = '' } = useParams();
	const location = useLocation();
	const { shop } = useShop();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();
	const { billing } = useBillingStatus(shop?.id);
	const { shopPath } = useShopPath();
	const goBack = useGoBack(`/channels/${id}`);

	if (!IN_SCOPE_CHANNEL_IDS.includes(id)) return <NotFound />;
	if (!shop?.id || channelsLoading || defsLoading) return <SetupLoading />;

	const channel = buildChannel(id, definitions, channels, billing?.entitlements);
	if (!channel) return <NotFound />;
	const stepParam = new URLSearchParams(location.search).get('step');
	// A live channel normally has nothing to set up — send the retailer to
	// /manage. Exception: Meta's post-enable "Create your shop" courtesy step
	// passes ?step=create-shop and needs to render here. Non-wizard channels
	// don't have a setup flow at all — send those back to the detail page.
	if (channel.live && !LIVE_ALLOWED_STEPS.has(stepParam)) return <Navigate to={shopPath(`/channels/${id}/manage`)} replace />;
	if (channel.enablement !== 'wizard') return <Navigate to={shopPath(`/channels/${id}`)} replace />;

	const SetupComponent = SETUP_COMPONENTS[id];
	if (!SetupComponent) return <Navigate to={shopPath(`/channels/${id}`)} replace />;

	return (
		<div className="space-y-8">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" /> Back
			</button>

			<header className="flex items-start gap-4">
				<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-12 w-12" />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">Set up {channel.title}</h1>
						{channel.pending ? (
							<span className="rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold text-action-soft-foreground">
								Setup pending
							</span>
						) : null}
					</div>
					<p className="mt-1.5 max-w-3xl text-base text-muted-foreground">{channel.description}</p>
				</div>
			</header>

			<SoftCard className="!p-6 md:!p-8">
				<SetupComponent channel={channel} shop={shop} />
			</SoftCard>
		</div>
	);
}
